import { ApiClient } from './api/client';
import { DUMMY_RANKING } from './api/dummyRanking';
import { generateRandomNickname } from './api/nicknamePool';
import {
  LocalRankingService,
  MAX_DAILY_PLAYS,
  RemoteRankingService,
  type MyProfile,
  type RankingEntry,
  type RankingService,
} from './api/ranking';
import {
  DEV_TEST_CREDENTIALS,
  isExplicitDevQuery,
  resolveApiBaseUrl,
} from './config/env';
// 최상단 import → 토큰 리스너가 게임 선택/번들 로드 전에 등록됨(토큰 레이스 방지).
import { onAuthToken, waitForAuthToken } from './api/authToken';
import { CONFIG } from './config/gameConfig';
import { Game } from './game/Game';
import { RankingTracker } from './game/RankingTracker';
import { filterDisplayedRankings } from './game/rankingFilter';
import { Hud } from './ui/Hud';
import { mountLoadingLottie } from './ui/LottieLoading';
import { showGameOverScreen, showStartScreen } from './ui/screens';
import { Toast } from './ui/Toast';

const RANKING_FETCH_LIMIT = 500;

const PARENT_TOKEN_WAIT_MS = 2000;

interface AuthTokens {
  accessToken: string;
  refreshToken: string;
}

async function bootstrap(): Promise<void> {
  const canvas = document.getElementById('game');
  const frame = document.getElementById('game-frame');
  if (!(canvas instanceof HTMLCanvasElement)) {
    throw new Error('#game canvas element not found');
  }
  if (!frame) {
    throw new Error('#game-frame element not found');
  }

  const baseUrl = resolveApiBaseUrl();
  if (baseUrl === null) {
    showAccessErrorScreen(frame);
    return;
  }

  // HUD 를 가장 먼저 생성해 로딩 오버레이 표시. bloomingo-fe 와 동일한 Lottie 사용.
  const hud = new Hud(frame);
  const loadingHost = hud.showOverlay('<div data-loading></div>');
  const loadingTarget = loadingHost.querySelector<HTMLElement>('[data-loading]');
  const loadingAnim = loadingTarget ? mountLoadingLottie(loadingTarget) : null;

  const { client, ranking } = createRanking(baseUrl);

  // 인증 흐름:
  //   - '?env=dev' 직접 진입: 하드코딩 테스트 계정으로 자동 로그인
  //   - iframe 진입 (dev/prod 공통): FE 가 postMessage 로 보내는 토큰을 짧게 대기
  // 리스너는 두 경로 모두 등록 — 이후 토큰 갱신을 위해 계속 살아 있음.
  if (client !== null) {
    onAuthToken((token) => client.setBearerToken(token));

    if (isExplicitDevQuery()) {
      try {
        const tokens = await client.post<AuthTokens, typeof DEV_TEST_CREDENTIALS>(
          '/auth/tokens',
          DEV_TEST_CREDENTIALS,
        );
        client.setBearerToken(tokens.accessToken);
      } catch (error) {
        console.warn('[bloomingo-game] dev 자동 로그인 실패:', error);
      }
    } else {
      await waitForAuthToken(PARENT_TOKEN_WAIT_MS);
    }
  }

  // 게임 시작 전에 BE record 를 확실히 만들어둔다.
  // (이후 첫 게임오버 submit 이 GAME_RECORD_NOT_FOUND 로 실패하지 않도록.)
  let initialProfile: MyProfile = {
    userId: '',
    nickname: null,
    bestScore: 0,
    playCount: 0,
    playsRemainingToday: MAX_DAILY_PLAYS,
  };
  try {
    initialProfile = await ranking.fetchMyProfile();
    if (!initialProfile.nickname || initialProfile.nickname.length === 0) {
      initialProfile = await ranking.updateNickname(generateRandomNickname());
    }
  } catch (error) {
    console.warn('[bloomingo-game] 사전 닉네임 등록 실패:', error);
  }

  loadingAnim?.destroy();

  const toast = new Toast(frame);
  const tracker = new RankingTracker();
  let allEntries: ReadonlyArray<RankingEntry> = [];
  let cachedProfile: MyProfile = initialProfile;
  hud.setHearts(cachedProfile.playsRemainingToday);

  const game = new Game(canvas, {
    onScoreChange: (score, _highScore) => {
      hud.setScore(score);
      hud.setRank(currentRank(score, allEntries), allEntries.length + 1);
      tracker.onScore(score, {
        onOvertake: (companyName, nickname) =>
          toast.show(
            { primary: 'Passed', secondary: `${companyName} / ${nickname}` },
            'overtake',
          ),
        onPersonalBest: () => toast.show({ primary: '내 기록 갱신' }, 'best'),
      });
    },
    onStateChange: (state) => {
      if (state === 'waiting') {
        toast.hide();
        hud.clearRank();
        hud.setHearts(cachedProfile.playsRemainingToday);
        game.setStartAllowed(cachedProfile.playsRemainingToday > 0);
        void showStartScreen(hud, ranking, cachedProfile);
      } else if (state === 'running') {
        hud.hideOverlay();
        toast.hide();
        void primeRunData(game, tracker, ranking, (entries, profile) => {
          allEntries = entries;
          cachedProfile = profile;
          hud.setRank(currentRank(0, entries), entries.length + 1);
          hud.setHearts(profile.playsRemainingToday);
        });
      }
    },
    onGameOver: (score) => {
      toast.hide();
      void handleGameOver(
        score,
        ranking,
        hud,
        cachedProfile,
        (next) => {
          cachedProfile = next;
          hud.setHearts(next.playsRemainingToday);
          game.setStartAllowed(next.playsRemainingToday > 0);
        },
        (entries) => {
          allEntries = entries;
        },
      );
    },
  });

  game.setStartAllowed(cachedProfile.playsRemainingToday > 0);
  game.start();
  canvas.focus();
}

async function primeRunData(
  game: Game,
  tracker: RankingTracker,
  ranking: RankingService,
  setRunData: (entries: ReadonlyArray<RankingEntry>, profile: MyProfile) => void,
): Promise<void> {
  const [entries, profile] = await Promise.all([
    ranking.fetchTop(RANKING_FETCH_LIMIT).catch(() => [] as RankingEntry[]),
    ranking
      .fetchMyProfile()
      .catch(
        () =>
          ({
            userId: '',
            nickname: null,
            bestScore: 0,
            playCount: 0,
            playsRemainingToday: MAX_DAILY_PLAYS,
          }) as MyProfile,
      ),
  ]);
  const displayed = filterDisplayedRankings(entries, {
    minScore: CONFIG.grave.minScore,
    minScoreGap: CONFIG.grave.minScoreGap,
    alwaysShowTopN: CONFIG.grave.alwaysShowTopN,
  });
  game.setUpcomingGraves(displayed);
  tracker.setRankings(displayed);
  tracker.beginRun(profile.bestScore);
  // BE 에 닉네임이 없으면 풀에서 생성해 메모리에만 캐시한다.
  // 실제 BE 등록(PATCH /me/nickname)은 게임오버 시 submit 재시도 흐름에서 처리 —
  // 게임이 너무 빨리 끝났을 때의 race 를 피하기 위함.
  const ensured: MyProfile =
    profile.nickname && profile.nickname.length > 0
      ? profile
      : { ...profile, nickname: generateRandomNickname() };
  setRunData(entries, ensured);
}

async function handleGameOver(
  score: number,
  ranking: RankingService,
  hud: Hud,
  profile: MyProfile,
  onProfileUpdated: (next: MyProfile) => void,
  onRankingsUpdated: (entries: ReadonlyArray<RankingEntry>) => void,
): Promise<void> {
  let registered: MyProfile = profile;
  let latestRankings: RankingEntry[] = [];
  try {
    const result = await ranking.submit({ score });
    registered = {
      userId: profile.userId,
      nickname: result.nickname,
      bestScore: result.bestScore,
      playCount: result.playCount,
      playsRemainingToday: result.playsRemainingToday,
    };
    latestRankings = result.rankings;
    onProfileUpdated(registered);
    onRankingsUpdated(result.rankings);
  } catch (error) {
    console.warn('[bloomingo-game] 자동 등록 실패:', error);
  }
  await showGameOverScreen(
    hud,
    ranking,
    score,
    registered,
    latestRankings,
    onProfileUpdated,
    onRankingsUpdated,
  );
}

function currentRank(score: number, entries: ReadonlyArray<RankingEntry>): number {
  let above = 0;
  for (const entry of entries) {
    if (entry.score > score) above += 1;
  }
  return above + 1;
}

function createRanking(
  baseUrl: string,
): { client: ApiClient | null; ranking: RankingService } {
  if (baseUrl.length > 0) {
    const client = new ApiClient({ baseUrl });
    return { client, ranking: new RemoteRankingService(client) };
  }
  return { client: null, ranking: new LocalRankingService(DUMMY_RANKING) };
}

function showAccessErrorScreen(frame: HTMLElement): void {
  frame.innerHTML = `
    <div class="access-error">
      <h1>잘못된 접근</h1>
      <p>블루밍고의 정상 경로에서 게임을 열어주세요.</p>
    </div>
  `;
}

// '/' 엔트리에서 어떤 게임을 띄울지 결정한다.
// 모든 환경에서 BE의 GET /games/active(월별 스케줄)로 결정한다.
// 조회 실패·미지정·비정상 접근(baseUrl null) 시 블루미 러너로 폴백.
async function selectAndLoadGame(): Promise<void> {
  const baseUrl = resolveApiBaseUrl();
  let slug = 'bloomy-runner';
  if (baseUrl !== null && baseUrl.length > 0) {
    try {
      const res = await fetch(`${baseUrl}/games/active`);
      if (res.ok) {
        const data = (await res.json()) as { slug?: string };
        if (data.slug) slug = data.slug;
      }
    } catch {
      // 활성 게임 조회 실패 시 기본 게임으로 폴백
    }
  }
  if (slug === 'poop-dodge') {
    void import('./games/poop-dodge/main');
  } else {
    void bootstrap();
  }
}

void selectAndLoadGame();
